// utils/formatAIText.tsx

import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const normalizeAiMarkdown = (text: string) => {
    if (!text) return "";

    return (
        text
            .replace(/\r\n/g, "\n")

            // Remove excessive spacing
            .replace(/\n{3,}/g, "\n\n")

            // Normalize separators
            .replace(/\n-{3,}\n/g, "\n---\n")

            // Remove trailing spaces
            .replace(/[ \t]+$/gm, "")

            // Convert malformed bullet-number combos
            // Example:
            // - 1. Something
            // becomes:
            // 1. Something
            .replace(/^\s*[-•]\s+(\d+\.)/gm, "$1")

            // Remove bullet before alphabetic step markers
            // - a. Step
            .replace(/^\s*[-•]\s+([a-zA-Z]\.)/gm, "$1")

            .trim()
    );
};

export const formatAIText = (text: string) => {
    const formattedText = normalizeAiMarkdown(text);

    return (
        <div className="ai-markdown-content">
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    h1: ({ children }) => (
                        <h1 className="text-[26px] font-bold text-[#111827] mt-8 mb-4 leading-snug">
                            {children}
                        </h1>
                    ),

                    h2: ({ children }) => (
                        <h2 className="text-[22px] font-semibold text-[#111827] mt-7 mb-4 leading-snug">
                            {children}
                        </h2>
                    ),

                    h3: ({ children }) => (
                        <h3 className="text-[18px] font-semibold text-[#111827] mt-6 mb-3 leading-snug">
                            {children}
                        </h3>
                    ),

                    h4: ({ children }) => (
                        <h4 className="text-[16px] font-semibold text-[#111827] mt-5 mb-2 leading-snug">
                            {children}
                        </h4>
                    ),

                    p: ({ children }) => (
                        <p className="text-[16px] leading-7 text-[#374151] m-0 whitespace-pre-wrap break-words">
                            {children}
                        </p>
                    ),

                    strong: ({ children }) => (
                        <strong className="font-semibold text-[#111827]">
                            {children}
                        </strong>
                    ),

                    em: ({ children }) => (
                        <em className="italic text-[#4B5563]">
                            {children}
                        </em>
                    ),

                    hr: () => (
                        <hr className="my-6 border-0 border-t border-[#E5E7EB]" />
                    ),

                    ul: ({ children }) => (
                        <ul className="list-disc pl-6 text-[#374151]">
                            {children}
                        </ul>
                    ),

                    ol: ({ children }) => (
                        <ol className="list-decimal pl-6 text-[#374151]">
                            {children}
                        </ol>
                    ),

                    li: ({ children }) => (
                        <li className="leading-7 py-1">
                            <div className="[&>p]:m-0 [&>p]:inline [&>ul]:mt-1 [&>ol]:mt-1">
                                {children}
                            </div>
                        </li>
                    ),

                    blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-yellow-500 bg-yellow-50 px-4 py-3 rounded-r-lg my-5 text-[#92400E]">
                            {children}
                        </blockquote>
                    ),

                    code: ({ className, children }) => {
                        const isBlockCode = className?.includes("language-");

                        if (!isBlockCode) {
                            return (
                                <code className="bg-[#F3F4F6] text-[#DC2626] px-1.5 py-1 rounded text-[14px] font-mono">
                                    {children}
                                </code>
                            );
                        }

                        return (
                            <code className="text-[14px] font-mono">
                                {children}
                            </code>
                        );
                    },

                    pre: ({ children }) => (
                        <pre className="bg-[#111827] text-white p-4 rounded-xl overflow-x-auto my-5 text-[14px] leading-7">
                            {children}
                        </pre>
                    ),

                    table: ({ children }) => (
                        <div className="overflow-x-auto my-5">
                            <table className="w-full border border-[#E5E7EB] rounded-lg overflow-hidden">
                                {children}
                            </table>
                        </div>
                    ),

                    thead: ({ children }) => (
                        <thead className="bg-[#F9FAFB]">
                            {children}
                        </thead>
                    ),

                    tbody: ({ children }) => (
                        <tbody className="divide-y divide-[#E5E7EB]">
                            {children}
                        </tbody>
                    ),

                    tr: ({ children }) => (
                        <tr>{children}</tr>
                    ),

                    th: ({ children }) => (
                        <th className="px-4 py-3 text-left text-[14px] font-semibold text-[#111827] border-b border-[#E5E7EB]">
                            {children}
                        </th>
                    ),

                    td: ({ children }) => (
                        <td className="px-4 py-3 text-[14px] text-[#374151] align-top">
                            {children}
                        </td>
                    ),

                    a: ({ href, children }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 hover:underline break-all"
                        >
                            {children}
                        </a>
                    ),
                }}
            >
                {formattedText}
            </ReactMarkdown>
        </div>
    );
};